import React from 'react'

import {
  BarChart,
  Box,
  Card,
  ChartColorType,
  Grid,
  InfoDot,
  Text,
} from '@factorialco/deprecated-design-system'
import { max, min } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import { AverageScore, LowPerformers, TopPerformers } from './numericInfo'
import { PERFORMANCE_COLORS, ScoreCount, calculatePerformancePercentile } from './scorePerformance'

interface LegendInfo {
  low: InfoType
  normal: InfoType
  high: InfoType
}

interface InfoType {
  color: ChartColorType
  text: string
}

const INDEX_BY = 'score'

const PERF_TYPES: (keyof LegendInfo)[] = ['low', 'normal', 'high']

const Legend: React.FC<{ type: keyof LegendInfo }> = ({ type }) => {
  const LEGEND_INFO: LegendInfo = {
    low: {
      color: PERFORMANCE_COLORS.low,
      text: i18n.t('performance_process.insights.alignment.low'),
    },
    normal: {
      color: PERFORMANCE_COLORS.normal,
      text: i18n.t('performance_process.insights.alignment.normal'),
    },
    high: {
      color: PERFORMANCE_COLORS.top,
      text: i18n.t('performance_process.insights.alignment.high'),
    },
  }

  const info = LEGEND_INFO[type]
  return (
    <Box gap="s4" flexDirection="row">
      <Box flexNoShrink>
        <InfoDot size="medium" color={info.color} />
      </Box>
      <Text.Normal capitalize weight="medium" size="100">
        {info.text}
      </Text.Normal>
    </Box>
  )
}

export const GaussBell: React.FC<{
  title: string
  data: { count: ScoreCount[]; average: number; total: number }
}> = ({ title, data }) => {
  if (data.total == 0) return null

  const scores = data.count.map((c) => c.score)

  const minScale = min(scores) ?? 0
  const maxScale = max(scores) ?? 0

  const performersPercent = calculatePerformancePercentile({
    minScale,
    maxScale,
    data: data.count,
    total: data.total,
  })

  return (
    <Card>
      <Card.Title>{title}</Card.Title>
      <Card.Content>
        <Box maxHeight="s600" width="full" overflowY="auto" padding="s16">
          <Grid variant="fixed">
            <Box justifyContent="flexStart" gridColSpan={{ xxs: 2 }} gap="s32">
              <AverageScore score={data.average} maxScore={maxScale} />
              <TopPerformers score={performersPercent.top} />
              <LowPerformers score={performersPercent.low} />
            </Box>
            <Box gridColSpan={{ xxs: 10 }} gap="s8">
              <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
                <Box paddingLeft="s24">
                  <Text size="100" weight="medium" color="grey800">
                    N° {i18n.t('performance_process.insights.charts.reports')}
                  </Text>
                </Box>
                <Box flexDirection="row" gap="s16">
                  {PERF_TYPES.map((type) => (
                    <Legend type={type} key={type} />
                  ))}
                </Box>
              </Box>
              <Box height="s300">
                <BarChart
                  margin={{ top: 8, bottom: 40, left: 40, right: 0 }}
                  colors={data.count.map((c) => c.color)}
                  colorBy="indexValue"
                  maxValue={data.total}
                  keys={['count']}
                  indexBy={INDEX_BY}
                  data={data.count}
                />
              </Box>
            </Box>
          </Grid>
        </Box>
      </Card.Content>
    </Card>
  )
}
